// A per-TAB identity for this client, sent on every API request as `X-Client-Id`.
//
// It exists for one job: letting a realtime frame say which tab caused the
// change, so that tab can ignore its own echo while every other surface acts on
// it. That sounds like a nicety until you look at what it replaces.
//
// ⚠ WHY NOT `excludeUserId`, WHICH THE HUB ALREADY HAS. Excluding the actor's
// user id was the first attempt, and it breaks the projector view — the shared
// screen in the room is almost always driven by the ORGANIZER's own account, on
// their laptop, while they draw the round from their phone. Excluding by user id
// excludes every socket they own, so the one screen the whole room is watching
// is the one screen that never updates. Nothing errors; it just silently stops
// being live for the person running the tournament.
//
// ⚠ WHY NOT A TIME WINDOW ("ignore frames within 500ms of my own write"). That
// is a heuristic about network timing, which is the class of thing that works on
// a desk and fails in a room with 100 phones on one access point.
//
// So: per tab, not per user, and not per session. A reload keeps it (the tab is
// still the same surface, and `sessionStorage` is per tab); a second tab gets
// its own, which is exactly what makes the board update while the editor does
// not.
//
// It is an opaque random token. It is not a credential, it authorizes nothing,
// and the server never stores it — it echoes it into one frame and forgets it.

let cached: string | null = null;

const KEY = 'msc.clientId';

function makeId(): string {
  // `randomUUID` needs a secure context, which a plain-http dev server is not.
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * This tab's client id.
 *
 * Every read and write of `sessionStorage` is wrapped, because it THROWS rather
 * than returning null in some contexts — a browser set to block site data, and
 * some embedded webviews. A client id is a convenience, so failing to persist it
 * must degrade to an in-memory one rather than break every API call in the app.
 */
export function clientId(): string {
  if (cached !== null) return cached;
  try {
    const stored = window.sessionStorage.getItem(KEY);
    if (stored) {
      cached = stored;
      return stored;
    }
  } catch {
    /* storage unavailable; fall through to a memory-only id */
  }

  const fresh = makeId();
  cached = fresh;
  try {
    window.sessionStorage.setItem(KEY, fresh);
  } catch {
    /* memory-only for this page's lifetime, which is enough to ignore an echo */
  }
  return fresh;
}
